import { filter, tap, switchMap } from 'rxjs/operators';
import { Component, Input, OnInit } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl
} from '@angular/forms';
import { Subject, Observable, merge, of } from 'rxjs';

import * as fromRoot from '../../reducers';
import * as forms from '../../actions/forms';
import * as support from '../../actions/support';
import * as utils from '../../shared/utils';
import * as notifications from '../../actions/notifications';

@Component({
  selector: 'edge-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  @Input() sport: string = '';
  @Input() email: string = '';
  @Input() vcode: string = '';

  formName = 'resetPassword';
  submitted = false;
  public resetPasswordForm: FormGroup;

  validationMessages: any = {
    emailAddress: {
      required: 'Email Address is required',
      email: 'Not a valid email address'
    },
    verificationCode: { required: 'Verification Code is required' },
    password: {
      required: 'Please provide your password.',
      minlength: 'Password should be 8-15 characters',
      maxlength: 'Password should be 8-15 characters'
    },
    confirmPassword: {
      required: 'Confirm Password is required',
      mismatch: 'Passwords do not match'
    }
  };

  action$ = new Subject();
  formAction$: Observable<Action> = this.action$.pipe(
    tap(() => (this.submitted = true)),
    switchMap((data) =>
      of(forms.FormRequestAction(utils.fromForm(this.formName, data))).pipe(
        filter(() => this.resetPasswordForm.valid)
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private formBuilder: FormBuilder
  ) {
    merge(this.formAction$).subscribe(store);
    store.dispatch(forms.FormCreateAction({ formName: this.formName }));
    this.buildResetPasswordForm();
  }

  buildResetPasswordForm(): void {
    this.resetPasswordForm = this.formBuilder.group({
      emailAddress: [
        '',
        Validators.compose([Validators.required, Validators.email])
      ],
      verificationCode: ['', Validators.required],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(15)
        ])
      ],
      confirmPassword: [
        '',
        Validators.compose([Validators.required, this.passwordMatcher])
      ]
    });

    this.resetPasswordForm.valueChanges.subscribe(() => this.onValueChanged());
    this.onValueChanged();
  }

  onValueChanged(): void {
    if (!this.resetPasswordForm) {
      return;
    }
    const form = this.resetPasswordForm;
    if (
      form.value.password === form.value.confirmPassword &&
      form.value.confirmPassword !== ''
    ) {
      form.controls.confirmPassword.updateValueAndValidity({
        onlySelf: true
      });
    }
  }

  passwordMatcher(control: AbstractControl): any {
    const password = control.root.get('password');
    const confirm = control.root.get('confirmPassword');
    if (!password || !confirm) {
      return null;
    }
    return password.value === confirm.value ? null : { mismatch: true };
  }

  passwordValidator(control: AbstractControl): any {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{8,}/;
    return control.value.match(passwordRegex)
      ? null
      : { passwordStrength: true };
  }

  hasError(key: string, type: string): boolean {
    const control = this.resetPasswordForm.controls[key];
    return control.errors ? control.errors[type] : false;
  }

  getErrorMessage(key: string, type: string): string {
    return this.validationMessages[key][type];
  }

  onSubmit(): void {
    if (!this.resetPasswordForm.valid) {
      this.store.dispatch(
        notifications.AddFailureNotification(
          'Please fix the form errors and try again.'
        )
      );
    }
    this.action$.next(this.resetPasswordForm.value);
  }

  ngOnInit(): void {
    this.store.dispatch(support.ToggleSupportPageAction(this.sport));
    this.resetPasswordForm.controls.emailAddress.setValue(this.email);
    this.resetPasswordForm.controls.verificationCode.setValue(this.vcode);
  }
}
